import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import { IPostVehicle, IVehicleDetails } from '../../../../shared/services/vehicle/vehicle.model';
import VehicleService from '../../../../shared/services/vehicle/vehicle.service';
import { VehicleForm } from '../../../presentation-components/organisation-components/vehicle-form';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { Box, Tab, Tabs } from '@mui/material';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { LoadingPage } from '../../loading-page';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import { NotesPanel } from '../../../presentation-components/notes-components/notes-panel';
import { useNotePeriodOptions } from '../../../presentation-components/notes-components/use-note-period-options';
import { UserDetailsContext } from '../../../../context/user-details/user-details';

export const OrganisationVehicleEditPage = () => {
  const navigate = useNavigate()
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams()
  //Service
  const userNotificationsContext = useContext(UserNotificationsContext)
  const vehicleService = VehicleService.getInstance();
  const reportPeriodContext = useContext(ReportPeriodContext);
  const screenLabelsContext = useContext(ScreenLabelsContext);
  const userDetailsContext = useContext(UserDetailsContext);
  const notePeriodOptions = useNotePeriodOptions();
  // Vehicle notes are written by admins and analysts (a data owner has no vehicle scope).
  const canWriteNotes = !!userDetailsContext.userDetails?.roles?.some((role) => role === 'admin' || role === 'analyst');
  //State
  const [value, setValue] = React.useState(0)
  const [vehicle, setVehicle] = useState<IVehicleDetails>();
  const [editVehicle, setEditVehicle] = useState<IPostVehicle>()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [errorCode, setErrorCode] = useState<number>()
  const [editField, setEditField] = useState<string>('')
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasOpenReport, setHasOpenReport] = useState<boolean>(true)
  //Loading State
  const [isFormSubmitting, setIsFormSubitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Use Effect
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        const tabIndex = parseInt(token)
        setValue(tabIndex)
        if (tabIndex === 1) {
          setIsLoading(false)
        }
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
  }, [])

  useEffect(() => {
    if (id && value === 0) {
      getVehicle(id)
    }
  }, [reportPeriodContext.lastSelectedReportId, id, value])

  const getVehicle = (id: string) => {
    vehicleService.getOneVehicle(id)
      .then(response => {
        setVehicle(response)
        setEditVehicle({
          vinNumber: response.vinNumber,
          customUniqueId: response.customUniqueId,
          subsidiaryId: response.subsidiaryId,
          organisationId: response.organisationId,
          dataEntryPeriod: response.dataEntryPeriod,
          vehicleType: response.vehicleType,
          assignedUserId: response.assignedUserId,
          startDate: response.startDate,
          endDate: response.endDate,
          ownership: response.ownership,
          vehicleReference: response.vehicleReference,
          customFieldValues: response.customFieldValues,
          conversionId: response.conversionId,
          conversionSourceId: response.conversionSourceId,
          conversion: response.conversion,
          conversionSource: response.conversionSource,
        })
      })
      .catch(err => {
        console.log(err)
      })
  }

  const submitHandler = async (editVehicleForm: IPostVehicle) => {
    if (id && editVehicle) {
      vehicleService.editVehicle(id, editVehicleForm)
        .then(response => {
          getVehicle(response.id!)
          refreshVehicles();
          setEditField('')
        })
        .catch(err => {
          console.log(err)
          if (err.response.data.message) {
            setErrorMessage(err.response.data.message)
            setErrorCode(err.response.data.code)
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          } else {
            userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
          }
        })
    }
  }

  function refreshVehicles(): void {
    vehicleService.getAllVehicles(true)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message: 'Updated successfully' });
      })
      .catch(err => {
        console.log(err)
      })
  }

  const deleteHandler = (id: string, isInUse: boolean) => {
    if (isInUse) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Required',
        message: `You are unable to delete this vehicle as it has or currently is in use. Please review and update any associated emissions before attempting to delete it.`,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      userNotificationsContext.openDialog({ active: true, title: 'Confirmation', message: `Are you sure you want to delete this vehicle? This action cannot be undone.`, buttonCopy: 'Continue', cancelButtonCopy: 'Cancel' })
        .then(response => {
          if (response) {
            if (!isFormSubmitting) {
              setIsFormSubitting(true)
              vehicleService.remove(id)
                .then(() => {
                  userNotificationsContext.openSnackbar({ open: true, message: 'Deleted successfully' });
                  navigate('/organisation/vehicles')
                  setIsFormSubitting(false)
                  refreshVehicles()
                })
                .catch(err => {
                  if (err.response.data.code === 1) {
                    userNotificationsContext.openDialog({
                      active: true, title: 'Action Not Allowed',
                      message: `This ${screenLabelsContext.getLabel('vehicle', { plural: false })} cannot be deleted because it is currently in use, or has been used previously.`,
                      buttonCopy: 'Ok',
                      hideCancelButton: true
                    })
                  } else {
                    userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                  }
                  setIsFormSubitting(false)
                  console.log(err)
                })
            }
          }
        })
    }
  }

  const deactivateHandler = (id: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `By deactivating this ${screenLabelsContext.getLabel('vehicle', { plural: false })}, you will not be able to add any more emissions to the ${screenLabelsContext.getLabel('vehicle', { plural: false })}. This action cannot be undone.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response) {
          if (!isFormSubmitting) {
            setIsFormSubitting(true)
            vehicleService.deactivate(id)
              .then(response => {
                getVehicle(id)
                userNotificationsContext.openSnackbar({ open: true, message: 'Deactivated successfully' });
                setIsFormSubitting(false)
              })
              .catch(err => {
                userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
                console.log(err)
                setIsFormSubitting(false)
              })
          }
        }
      })
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  };

  return (
    <PageOuterContainer>
      {vehicle &&
        <>
          <PageHeading
            title={`${screenLabelsContext.getLabel('vehicle', { plural: false, capitalize: 'first' })} ${vehicle?.customUniqueId}`}
            includeBackButton={true}
            backButtonLink='/organisation/vehicles'
            backButtonText={`Back to ${screenLabelsContext.getLabel('vehicle', { plural: true })}`}
            hasTabs={true}
            includeActionButton={true}
            actionButtonIcon={IconName.HideSourceOutlined}
            actionButtonHandler={() => deactivateHandler(vehicle.id)}
            actionButtonText='Deactivate'
            hideShowActionButton={!vehicle.active}
            includeSecondaryActionButton={true}
            secondaryActionButtonIcon={IconName.DeleteOutline}
            secondaryActionButtonHandler={() => deleteHandler(vehicle.id, vehicle.vehicleInUse)}
            secondaryActionButtonText='Delete'
          />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details'  {...a11yProps(0)} sx={{ borderRadius: 1 }} />
              <Tab label='Emissions' {...a11yProps(1)} sx={{ borderRadius: 1 }} />
              <Tab label='Notes' {...a11yProps(2)} sx={{ borderRadius: 1 }} />
            </Tabs>
          </Box>
          <CustomTabPanel value={value} index={0}>
            {editVehicle &&
              <VehicleForm
                isEditPage={true}
                submitHandler={submitHandler}
                newVehicle={editVehicle}
                vehicleActive={vehicle.active}
                errorCode={errorCode}
                errorMessage={errorMessage}
                setErrorMessage={setErrorMessage}
                setErrorCode={setErrorCode}
                editField={editField}
                setEditField={setEditField}
              />
            }
          </CustomTabPanel>
          <CustomTabPanel value={value} index={1}>
            {!hasOpenReport &&
              <Box>
                < ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
                {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
              </Box>
            }
            {hasOpenReport &&
              <LoadingPage pageReady={!isLoading}>
                {reportPeriodContext.lastSelectedReportId && id && (
                  <Emissions
                    includeDelete={false}
                    includeEdit={false}
                    queryString={`&vehicleId=${id}`}
                    backLink={`/organisation/vehicles/${id}?tabId=1`}
                    backText='Back to vehicle'
                  />
                )}
              </LoadingPage>}
          </CustomTabPanel>
          <CustomTabPanel value={value} index={2}>
            <NotesPanel
              relatedModelName='vehicle'
              relatedObjectId={vehicle.id}
              canWrite={canWriteNotes}
              periodOptions={notePeriodOptions}
            />
          </CustomTabPanel>
        </>}
    </PageOuterContainer>
  )
}